import { gql, skipToken, useSuspenseQuery } from '@apollo/client';
import { useMemo } from 'react';

const FEATURE_FLAGS_QUERY = gql`
    query FeatureFlags($features: [String!]!) {
        featureFlags(keys: $features) {
            id
            enabled
        }
    }
`;

// Returns a record of feature flag names to whether they are enabled. Feature flag values will be refetched when the auth state changes.
// If passed a key that is not actually a feature flag, it will return false.
export const useFeatureFlagsEnabled = (
    features: Record<string, string>,
    options?: { skip?: boolean },
) => {
    const featuresArray = useMemo(() => Object.values(features), [features]);

    const { data } = useSuspenseQuery<{
        featureFlags: { id: keyof typeof features; enabled: boolean }[];
    }>(FEATURE_FLAGS_QUERY, options?.skip ? skipToken : { variables: { features: featuresArray } });

    // Transform the response into key-value pairs as follows:
    // {
    //     [feature1]: true,
    //     [feature2]: false,
    //     ...
    //     [featureN]: [featureN is enabled]
    // }
    //
    // If passed a key that is not actually a feature flag, it will return false.
    return Object.keys(features).reduce<Record<string, boolean>>((acc, key) => {
        const match = data?.featureFlags.find((flag) => flag.id === features[key]);
        acc[key] = match?.enabled ?? false;
        return acc;
    }, {});
};

// Convenience function for when you only have one feature flag.
export const useFeatureFlagEnabled = (feature: string, options?: { skip?: boolean }) => {
    return useFeatureFlagsEnabled({ [feature]: feature }, options)[feature];
};
